import { useEffect, useMemo, useRef } from "react";

const DEFAULT_IDLE_TIMEOUT_MS = 3_600_000;

function parseTimeoutMs(rawValue) {
  const n = Number(rawValue);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export function useIdleLogout({ enabled, onIdle, timeoutMs } = {}) {
  const timeoutFromEnv = useMemo(() => {
    if (typeof process === "undefined") return null;
    return parseTimeoutMs(process.env.REACT_APP_IDLE_TIMEOUT_MS);
  }, []);

  const effectiveTimeoutMs = timeoutMs ?? timeoutFromEnv ?? DEFAULT_IDLE_TIMEOUT_MS;

  const timerRef = useRef(null);
  const lastActivityAtRef = useRef(Date.now());

  useEffect(() => {
    if (!enabled) return;

    const clear = () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = null;
    };

    const arm = () => {
      clear();
      timerRef.current = window.setTimeout(() => {
        const now = Date.now();
        const elapsed = now - lastActivityAtRef.current;
        if (elapsed >= effectiveTimeoutMs) onIdle?.();
        else arm();
      }, effectiveTimeoutMs);
    };

    const markActivity = () => {
      lastActivityAtRef.current = Date.now();
      arm();
    };

    // Kullanıcı eylemi sayılanlar: sayfada etkileşim, menü tıklamaları (click), scroll, klavye vb.
    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
      "wheel",
    ];

    const listenerOptions = { passive: true };
    for (const evt of events) document.addEventListener(evt, markActivity, listenerOptions);

    // İlk arm (sayfa açılır açılmaz sayaç başlasın)
    arm();

    return () => {
      clear();
      for (const evt of events) document.removeEventListener(evt, markActivity, listenerOptions);
    };
  }, [enabled, effectiveTimeoutMs, onIdle]);

  // Dışarıdan (ör. route değiştiğinde) manuel reset için.
  const resetRef = useRef(() => {});
  useEffect(() => {
    resetRef.current = () => {
      lastActivityAtRef.current = Date.now();
      if (enabled) {
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => onIdle?.(), effectiveTimeoutMs);
      }
    };
  }, [enabled, effectiveTimeoutMs, onIdle]);

  return useMemo(() => ({ timeoutMs: effectiveTimeoutMs, reset: () => resetRef.current() }), [effectiveTimeoutMs]);
}
